import * as THREE from 'three';
import { calcTextureSize, maxAnnotationTextureWidth } from './PointsAttributeBuffer';
import { RandomGenerator } from './RandomGenerator';

type ColorRGBA = [r: number, g: number, b: number, a: number];

const colorFromInt32 = (color: number): ColorRGBA => {
  // 0x11223344
  // r = 0x11
  // g = 0x22
  // b = 0x33
  // a = 0x44
  const r = (color >> 24) & 0xff;
  const g = (color >> 16) & 0xff;
  const b = (color >> 8) & 0xff;
  const a = color & 0xff;
  return [r, g, b, a];
};

const colorToInt32 = (color: ColorRGBA): number => {
  return (color[0] << 24) | (color[1] << 16) | (color[2] << 8) | color[3];
};

// String color API is deprecated
export const colorFromHexString = (hexColor: string): ColorRGBA => {
  const r = Number.parseInt(hexColor.slice(1, 3), 16);
  const g = Number.parseInt(hexColor.slice(3, 5), 16);
  const b = Number.parseInt(hexColor.slice(5, 7), 16);
  const a = Number.parseInt(hexColor.slice(7, 9), 16);
  return [r, g, b, a];
};

// String color API is deprecated
export const intColorFromHexString = (value: string | undefined) => {
  // "#11223344" => 0x11223344
  return value === undefined ? undefined : Number.parseInt(value.slice(1), 16);
};

// String color API is deprecated
export const intColorMapFromStringColorMap = (stringColorMap: Map<number, string> | undefined) => {
  if (stringColorMap === undefined) {
    return undefined;
  }
  const intColorMap = new Map<number, number>();
  stringColorMap?.forEach((value, key) => {
    const intValue = intColorFromHexString(value);
    if (intValue !== undefined) {
      intColorMap.set(key, intValue);
    }
  });
  return intColorMap;
};

// ColorMap represents color map for point cloud material
// It handles fast color updates via setColor(id, color)

export class ColorMap {
  public maxId: number;

  private textureSize: { width: number; height: number };

  private colorBuf: Uint8Array<ArrayBuffer>;

  public texture: THREE.DataTexture;

  private invalidateFrame: (() => void) | undefined;

  constructor(
    maxId: number,
    colors: Map<number, number> | undefined = undefined,
    defaultColor: number | undefined = undefined,
    invalidateFrame?: () => void
  ) {
    this.maxId = maxId;

    this.textureSize = calcTextureSize(maxId);
    const { width, height } = this.textureSize;

    this.colorBuf = this.createColorBuf(colors, defaultColor);

    this.texture = new THREE.DataTexture(
      this.colorBuf,
      width,
      height,
      THREE.RGBAFormat,
      THREE.UnsignedByteType,
      THREE.UVMapping,
      THREE.RepeatWrapping,
      THREE.RepeatWrapping,
      THREE.NearestFilter,
      THREE.NearestFilter,
      undefined,
      THREE.SRGBColorSpace
    );

    // this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;

    this.invalidateFrame = invalidateFrame || undefined;
  }

  public get cardinality() {
    const { height } = this.textureSize;
    return height === 1 ? 1 : 2;
  }

  public clear(defaultColor: number) {
    const { colorBuf } = this;
    // fill by default color
    const [r, g, b, a] = colorFromInt32(defaultColor);
    for (let index = 0; index < colorBuf.length; index += 4) {
      colorBuf[index + 0] = r;
      colorBuf[index + 1] = g;
      colorBuf[index + 2] = b;
      colorBuf[index + 3] = a;
    }

    this.texture.needsUpdate = true;
    if (this.invalidateFrame) this.invalidateFrame();
  }

  public setColor(id: number, color: number) {
    const [r, g, b, a] = colorFromInt32(color);
    const index = id * 4;
    this.colorBuf[index + 0] = r;
    this.colorBuf[index + 1] = g;
    this.colorBuf[index + 2] = b;
    this.colorBuf[index + 3] = a;

    this.texture.needsUpdate = true;
    if (this.invalidateFrame) this.invalidateFrame();
  }

  public getColor(id: number): number {
    const index = id * 4;
    const r = this.colorBuf[index + 0];
    const g = this.colorBuf[index + 1];
    const b = this.colorBuf[index + 2];
    const a = this.colorBuf[index + 3];

    return colorToInt32([r, g, b, a]);
  }

  private createColorBuf(colors?: Map<number, number>, defaultColor?: number) {
    const { width, height } = this.textureSize;
    const bufSize = width * height * 4;
    const colorBuf = new Uint8Array(bufSize);

    if (defaultColor) {
      // fill by default color
      const [r, g, b, a] = colorFromInt32(defaultColor);
      for (let index = 0; index < bufSize; index += 4) {
        colorBuf[index + 0] = r;
        colorBuf[index + 1] = g;
        colorBuf[index + 2] = b;
        colorBuf[index + 3] = a;
      }
    } else {
      // assign random colors
      const randGen = new RandomGenerator(123456789);
      for (let index = 0; index < bufSize; index += 4) {
        colorBuf[index + 0] = randGen.nextInt(256);
        colorBuf[index + 1] = randGen.nextInt(256);
        colorBuf[index + 2] = randGen.nextInt(256);
        colorBuf[index + 3] = 255;
      }
    }

    // update provided colors
    colors?.forEach((color, id) => {
      const index = id * 4;
      const [r, g, b, a] = colorFromInt32(color);
      colorBuf[index + 0] = r;
      colorBuf[index + 1] = g;
      colorBuf[index + 2] = b;
      colorBuf[index + 3] = a;
    });

    return colorBuf;
  }

  public setInvalidateFrame(newInvalidateFrame: () => void) {
    this.invalidateFrame = newInvalidateFrame;
  }
}

// All points are visible by default
export const defaultVisibilityColorMap = new ColorMap(
  // ensure (2D UV mapping), i.e. map size should be greater than max texture width
  maxAnnotationTextureWidth * 2,
  new Map<number, number>([[0, 0xffffffff]]),
  0xffffffff
);

export default ColorMap;
